import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import API from '../services/api';
import UserAvatar from './UserAvatar';
import { formatIsoDate } from '../utils/formatDateValue';
import {
  ChatBubbleLeftIcon,
  PaperAirplaneIcon,
  ArrowPathIcon,
  SparklesIcon,
  DocumentDuplicateIcon,
  DocumentTextIcon,
  PencilSquareIcon,
} from '@heroicons/react/24/outline';

// One chronological story of a case: internal comments (with the comment form), workflow and
// edit events from the audit log, AI validation runs, and duplicate-check runs. Comments are
// fetched here; audit entries are fetched when the role may read them; runs come from the
// panels that already load them.

export interface Actor { id?: string; firstName: string; lastName: string; role?: string; profilePicture?: string | null }
interface Comment { id: string; content: string; createdAt: string; author: Actor & { id: string; role: string } }
export interface AuditLog { id: string; action: string; entityType: string; oldValues?: any; newValues?: any; createdAt: string; user?: Actor; case?: { id: string; caseNumber: string } | null }
export interface ValidationRunSummary { id: string; decision: string; confidence: number; createdAt: string }
export interface DedupRunSummary { id: string; status: string; matchCount: number; createdAt: string; completedAt?: string; error?: string; triggeredBy?: Actor | null }

export type Kind = 'comment' | 'status' | 'edit' | 'ai' | 'dedup' | 'document';
export interface Event { id: string; kind: Kind; at: string; actor?: Actor | null; title: string; detail?: string; content?: string }

interface Props {
  caseId: string;
  // Bump to refetch comments (e.g. after the page posts a return-for-research note).
  refreshKey?: number;
  canViewAudit: boolean;
  validationRuns: ValidationRunSummary[];
  dedupRuns: DedupRunSummary[];
  // Called with the time of the most recent content edit in the audit log (kind "edit": field
  // changes, not status/assignment or AI-review actions), or null when there is none / no access.
  onLastEditAt?: (iso: string | null) => void;
  // The latest move to APPROVED / REJECTED in the audit log, for the AI panel's record view.
  onDecidedAt?: (decided: { at: string; by: string | null } | null) => void;
}

const FILTERS: { key: 'all' | Kind; label: string; kinds: Kind[] }[] = [
  { key: 'all', label: 'All', kinds: ['comment', 'status', 'edit', 'ai', 'dedup', 'document'] },
  { key: 'comment', label: 'Comments', kinds: ['comment'] },
  { key: 'status', label: 'Status', kinds: ['status', 'edit'] },
  { key: 'ai', label: 'AI', kinds: ['ai', 'dedup'] },
  { key: 'document', label: 'Documents', kinds: ['document'] },
];

const humanStatus = (s?: string) => {
  if (!s) return '';
  const map: Record<string, string> = { DRAFT: 'Draft', FOR_VALIDATION: 'For Validation', IN_RESEARCH: 'In Research', FOR_APPROVAL: 'For Approval', APPROVED: 'Approved', REJECTED: 'Rejected' };
  return map[s] || s;
};
const humanKey = (k: string) => k.replace(/([A-Z])/g, ' $1').replace(/^./, (c) => c.toUpperCase()).trim();
const NOISE_KEYS = new Set(['updatedAt', 'metadata', 'validationBypassed', 'reviewedById', 'submittedById']);

const timeAgo = (iso: string) => {
  const diffH = Math.floor((Date.now() - new Date(iso).getTime()) / 3600000);
  if (diffH < 1) return 'Just now';
  if (diffH < 24) return `${diffH}h ago`;
  if (diffH < 168) return `${Math.floor(diffH / 24)}d ago`;
  return formatIsoDate(iso) || iso;
};

// Turn an audit row into a readable event, or null for rows that add nothing for a reviewer.
export function auditToEvent(log: AuditLog): Event | null {
  const base = { id: `audit-${log.id}`, at: log.createdAt, actor: log.user };
  const nv = log.newValues || {};
  const ov = log.oldValues || {};
  if (log.entityType === 'Document') {
    if (log.action === 'CREATE') return { ...base, kind: 'document', title: `Uploaded document`, detail: nv.fileName };
    if (log.action === 'DELETE') return { ...base, kind: 'document', title: `Deleted document`, detail: ov.fileName || nv.fileName };
    return null;
  }
  if (log.entityType !== 'Case') return null;
  if (log.action === 'CREATE') return { ...base, kind: 'status', title: 'Case created', detail: nv.status ? `as ${humanStatus(nv.status)}` : undefined };
  if (log.action === 'SUBMIT') return { ...base, kind: 'status', title: 'Submitted for validation', detail: nv.validationBypassed ? 'completeness check waived' : undefined };
  if (log.action === 'MERGE') return { ...base, kind: 'dedup', title: nv.archivedDuplicate ? 'Archived as a duplicate' : 'Merged with a related case' };
  if (log.action === 'UPDATE') {
    // Reviewer actions on the AI ledger tag themselves in oldValues.source.
    const fieldLabel = ov.field ? humanKey(String(ov.field).split('.').pop()!.replace(/\[.*?\]/g, '')) : 'a field';
    if (ov.source === 'ai_confirm') return { ...base, kind: 'ai', title: nv.confirmed === false ? `Withdrew the check on AI value for ${fieldLabel}` : `Checked AI value for ${fieldLabel} against its source` };
    if (ov.source === 'ai_undo') return { ...base, kind: 'ai', title: `Undid AI auto-fill of ${fieldLabel}` };
    if (ov.source === 'ai_redo') return { ...base, kind: 'ai', title: `Re-applied AI auto-fill of ${fieldLabel}` };
    if (ov.source === 'ai_override') return { ...base, kind: 'ai', title: `Overrode AI value for ${fieldLabel}` };
    if (ov.source === 'ai_conflict_resolve') return { ...base, kind: 'ai', title: `Resolved AI source conflict on ${fieldLabel}` };
    if (ov.source === 'ai_finding_resolve') return { ...base, kind: 'ai', title: nv.resolution === 'not_applicable' ? 'Marked an AI blocker as not applicable' : 'Marked an AI blocker as addressed', detail: nv.note || undefined };
    if (ov.source === 'ai_finding_unresolve') return { ...base, kind: 'ai', title: 'Reopened an AI blocker' };
    if (nv.status && ov.status && nv.status !== ov.status) {
      const detail = nv.status === 'REJECTED' && nv.rejectionReason ? `${nv.rejectionCategory ? nv.rejectionCategory + ': ' : ''}${nv.rejectionReason}` : undefined;
      return { ...base, kind: 'status', title: `Status: ${humanStatus(ov.status)} → ${humanStatus(nv.status)}`, detail };
    }
    if (Array.isArray(nv.autoApplied)) {
      const n = nv.autoApplied.length;
      return { ...base, kind: 'ai', title: `AI auto-filled ${n} field${n === 1 ? '' : 's'}`, detail: nv.conflicts ? `${nv.conflicts} conflict${nv.conflicts === 1 ? '' : 's'} recorded` : undefined };
    }
    if (nv.applied !== undefined) return { ...base, kind: 'ai', title: 'AI suggestions applied' };
    if ('assignedToId' in nv) return { ...base, kind: 'status', title: nv.assignedToId ? 'Assigned' : 'Unassigned' };
    if (nv.priority !== undefined && Object.keys(nv).length === 1) return { ...base, kind: 'edit', title: `Priority set to ${nv.priority}` };
    const keys = Object.keys(nv).filter((k) => !NOISE_KEYS.has(k));
    if (keys.length === 0) return null;
    const shown = keys.slice(0, 5).map(humanKey).join(', ');
    return { ...base, kind: 'edit', title: 'Edited case', detail: keys.length > 5 ? `${shown} and ${keys.length - 5} more` : shown };
  }
  return null;
}

const KIND_ICON: Record<Kind, React.ReactNode> = {
  comment: <ChatBubbleLeftIcon className="h-4 w-4" aria-hidden="true" />,
  status: <ArrowPathIcon className="h-4 w-4" aria-hidden="true" />,
  edit: <PencilSquareIcon className="h-4 w-4" aria-hidden="true" />,
  ai: <SparklesIcon className="h-4 w-4" aria-hidden="true" />,
  dedup: <DocumentDuplicateIcon className="h-4 w-4" aria-hidden="true" />,
  document: <DocumentTextIcon className="h-4 w-4" aria-hidden="true" />,
};
const KIND_COLOR: Record<Kind, string> = {
  comment: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
  status: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
  edit: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
  ai: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
  dedup: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
  document: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
};
const ROLE_COLOR = (_role?: string) => 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200';

const PAGE = 20;

const ActivityCard: React.FC<Props> = ({ caseId, refreshKey = 0, canViewAudit, validationRuns, dedupRuns, onLastEditAt, onDecidedAt }) => {
  const { user } = useAuth();
  const { showToast } = useToast();
  const [comments, setComments] = useState<Comment[]>([]);
  const [audit, setAudit] = useState<AuditLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [newComment, setNewComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [filter, setFilter] = useState<'all' | Kind>('all');
  const [showAll, setShowAll] = useState(false);

  const fetchComments = async () => {
    try {
      const r = await API.get(`/api/cases/${caseId}/comments`);
      setComments(r.data.comments || []);
    } catch (e) {
      console.error('Failed to fetch comments:', e);
    }
  };
  const fetchAudit = async () => {
    if (!canViewAudit) return;
    try {
      const r = await API.get(`/api/audit-logs/case/${caseId}`);
      setAudit(r.data.logs || []);
    } catch (e) {
      console.error('Failed to fetch audit log:', e);
    }
  };
  useEffect(() => {
    setLoading(true);
    Promise.all([fetchComments(), fetchAudit()]).finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseId, refreshKey, canViewAudit]);

  useEffect(() => {
    if (!onLastEditAt) return;
    let last: string | null = null;
    for (const l of audit) {
      const ev = auditToEvent(l);
      if (ev?.kind === 'edit' && ev.title === 'Edited case' && (!last || l.createdAt > last)) last = l.createdAt;
    }
    onLastEditAt(last);
  }, [audit, onLastEditAt]);

  useEffect(() => {
    if (!onDecidedAt) return;
    let hit: AuditLog | null = null;
    for (const l of audit) {
      const st = l.newValues?.status;
      if ((st === 'APPROVED' || st === 'REJECTED') && (!hit || l.createdAt > hit.createdAt)) hit = l;
    }
    onDecidedAt(hit ? { at: hit.createdAt, by: hit.user ? `${hit.user.firstName} ${hit.user.lastName}`.trim() : null } : null);
  }, [audit, onDecidedAt]);

  const submitComment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newComment.trim()) return;
    setSubmitting(true);
    try {
      await API.post(`/api/cases/${caseId}/comments`, { content: newComment.trim(), isInternal: true });
      setNewComment('');
      await fetchComments();
    } catch (err: any) {
      showToast('error', err.response?.data?.message || 'Failed to add comment');
    } finally {
      setSubmitting(false);
    }
  };

  const events = useMemo<Event[]>(() => {
    const out: Event[] = [];
    for (const c of comments) out.push({ id: `c-${c.id}`, kind: 'comment', at: c.createdAt, actor: c.author, title: '', content: c.content });
    for (const l of audit) { const ev = auditToEvent(l); if (ev) out.push(ev); }
    for (const v of validationRuns) {
      const pct = Math.round((v.confidence ?? 0) * 100);
      const label = v.decision === 'NEEDS_REVIEW' ? 'Needs review' : v.decision.charAt(0) + v.decision.slice(1).toLowerCase();
      out.push({ id: `v-${v.id}`, kind: 'ai', at: v.createdAt, title: `AI validation: ${label}`, detail: `${pct}% confidence` });
    }
    for (const d of dedupRuns) {
      const status = d.status.charAt(0) + d.status.slice(1).toLowerCase();
      const detail = d.status === 'FAILED' ? (d.error || 'failed') : d.status === 'COMPLETED' ? `${d.matchCount} match${d.matchCount === 1 ? '' : 'es'}` : undefined;
      out.push({ id: `d-${d.id}`, kind: 'dedup', at: d.createdAt, actor: d.triggeredBy || undefined, title: `Duplicate check: ${status}`, detail });
    }
    return out.sort((a, b) => new Date(b.at).getTime() - new Date(a.at).getTime());
  }, [comments, audit, validationRuns, dedupRuns]);

  const activeKinds = FILTERS.find((f) => f.key === filter)!.kinds;
  const filtered = events.filter((e) => activeKinds.includes(e.kind));
  const visible = showAll ? filtered : filtered.slice(0, PAGE);
  const countFor = (kinds: Kind[]) => events.filter((e) => kinds.includes(e.kind)).length;

  return (
    <div id="section-activity" className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
      <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-4">Activity</h3>

      <form onSubmit={submitComment} className="mb-5">
        <div className="flex space-x-3">
          <div className="flex-shrink-0"><UserAvatar user={user} size="sm" /></div>
          <div className="flex-1">
            <textarea
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              placeholder="Add an internal comment..."
              rows={2}
              className="block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-emerald-500 focus:ring-emerald-500 text-sm"
            />
            <div className="mt-2 flex justify-end">
              <button
                type="submit"
                disabled={!newComment.trim() || submitting}
                className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md text-white bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50"
              >
                <PaperAirplaneIcon className="h-4 w-4 mr-1" aria-hidden="true" />
                {submitting ? 'Adding...' : 'Add Comment'}
              </button>
            </div>
          </div>
        </div>
      </form>

      {events.length > 0 && (
        <div role="tablist" className="flex flex-wrap gap-1 mb-3">
          {FILTERS.filter((f) => f.key === 'all' || countFor(f.kinds) > 0).map((f) => (
            <button
              key={f.key}
              type="button"
              role="tab"
              aria-selected={filter === f.key}
              onClick={() => { setFilter(f.key); setShowAll(false); }}
              className={`px-2.5 py-1 text-xs font-medium rounded-md ${filter === f.key ? 'bg-gray-900 text-white dark:bg-gray-100 dark:text-gray-900' : 'text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700'}`}
            >
              {f.label} <span className={filter === f.key ? 'opacity-70' : 'text-gray-400'}>({countFor(f.kinds)})</span>
            </button>
          ))}
        </div>
      )}

      {loading ? (
        <div className="text-center py-4">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-emerald-600 mx-auto"></div>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">Loading activity...</p>
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-6 text-gray-500 dark:text-gray-400">
          <ChatBubbleLeftIcon className="mx-auto h-8 w-8 text-gray-400 mb-2" aria-hidden="true" />
          <p className="text-sm">{events.length === 0 ? 'No activity yet' : 'Nothing in this filter'}</p>
          {events.length === 0 && <p className="text-xs mt-1">Be the first to add a comment</p>}
        </div>
      ) : (
        <ol className="space-y-4">
          {visible.map((ev) => (
            <li key={ev.id} className="flex gap-3">
              <div className="flex-shrink-0">
                {ev.kind === 'comment' && ev.actor ? (
                  <UserAvatar user={ev.actor as any} size="sm" />
                ) : (
                  <span className={`inline-flex h-8 w-8 items-center justify-center rounded-full ${KIND_COLOR[ev.kind]}`}>{KIND_ICON[ev.kind]}</span>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-sm">
                  {ev.actor ? (
                    <>
                      <span className="font-medium text-gray-900 dark:text-gray-100">{ev.actor.firstName} {ev.actor.lastName}</span>
                      {ev.actor.role && <span className={`px-1.5 py-0.5 text-[10px] font-medium rounded-full ${ROLE_COLOR(ev.actor.role)}`}>{ev.actor.role}</span>}
                    </>
                  ) : (
                    <span className="font-medium text-gray-700 dark:text-gray-300">System</span>
                  )}
                  <time dateTime={ev.at} title={formatIsoDate(ev.at) || ev.at} className="text-xs text-gray-500 dark:text-gray-400">{timeAgo(ev.at)}</time>
                </div>
                {ev.kind === 'comment' ? (
                  <div className="mt-1 text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{ev.content}</div>
                ) : (
                  <div className="mt-0.5 text-sm text-gray-700 dark:text-gray-300">
                    {ev.title}
                    {ev.detail && <span className="text-gray-500 dark:text-gray-400"> · {ev.detail}</span>}
                  </div>
                )}
              </div>
            </li>
          ))}
        </ol>
      )}
      {filtered.length > PAGE && (
        <button type="button" onClick={() => setShowAll((v) => !v)} className="mt-4 text-xs font-medium text-indigo-600 dark:text-indigo-400 hover:text-indigo-800">
          {showAll ? 'Show fewer' : `Show all (${filtered.length})`}
        </button>
      )}
    </div>
  );
};

export default ActivityCard;
